/**
 * Die Schallzeichen der Binnenschifffahrt als Zeitleiste.
 *
 * Die Längen stehen in der Vorbemerkung der Anlage 6 der Ordnung: ein kurzer Ton dauert
 * etwa eine Sekunde, ein langer etwa vier, die Pause zwischen zwei Tönen etwa eine. Die
 * Folge sehr kurzer Töne besteht aus mindestens sechs Tönen von je einer Viertelsekunde
 * mit ebenso langen Pausen, und eine Gruppe von Glockenschlägen dauert etwa vier Sekunden.
 *
 * Die Seesignale haben eigene Längen (`signals.ts`, Regel 32): dort dauert ein langer Ton
 * vier bis sechs Sekunden, und die App nimmt fünf. Beide Ordnungen bleiben getrennt,
 * damit keine die andere falsch klingen lässt.
 */
import type { BinnenSignalData } from './schema'
import type { SignalStep } from './signals'
import type { Anlage6Eintrag } from './sources'

export const KURZ_S = 1
export const LANG_S = 4
export const PAUSE_S = 1
export const SEHR_KURZ_S = 0.25
export const SEHR_KURZ_PAUSE_S = 0.25
export const GLOCKE_S = 4

type Element = BinnenSignalData['pattern'][number]

/**
 * Das Muster aus der Beschreibung der Anlage.
 *
 * Gelesen wird der Text und nicht das Symbol davor: die Symbole der Quelle sind nicht
 * verlässlich. Beim Überholen an Backbord steht "▬ ▬ ▪▪" für zwei lange und zwei kurze
 * Töne, also das Zeichen für sehr kurze, wo kurze gemeint sind, und beim Überholen an
 * Steuerbord fehlt das Symbol ganz.
 */
export function musterAus(beschreibung: string): { pattern: Element[]; repeatS: number } {
  const text = beschreibung.toLowerCase()
  if (/^kein (schall)?zeichen$/.test(text.trim())) return { pattern: [], repeatS: 0 }

  const repeatS = /jede minute/.test(text) ? 60 : /wiederhol/.test(text) ? 10 : 0

  if (/folge sehr kurzer töne/.test(text)) {
    return { pattern: [{ kind: 'sehr-kurz', count: 6 }], repeatS: 0 }
  }
  if (/glockenschläge/.test(text)) {
    return { pattern: [{ kind: 'glocke', count: 1 }], repeatS }
  }
  // "abwechselnd eines kurzen und eines langen Tones": das Bleib-Weg-Signal.
  if (/abwechselnd eines kurzen und eines langen/.test(text)) {
    return {
      pattern: [
        { kind: 'kurz', count: 1 },
        { kind: 'lang', count: 1 },
      ],
      repeatS: 1,
    }
  }
  if (/^wiederholte lange töne/.test(text)) {
    return { pattern: [{ kind: 'lang', count: 2 }], repeatS: 1 }
  }

  const zahl: Record<string, number> = { '1': 1, '2': 2, '3': 3, '4': 4, '5': 5 }
  const pattern: Element[] = []
  // "Ton" im Singular, "Töne" im Plural: der Umlaut wechselt mit der Zahl, und eine
  // Regel, die nur einen der beiden kennt, verliert den anderen still. So war aus
  // "1 langer Ton, 2 kurze Töne" zuerst "2 kurze" geworden.
  for (const teil of text.matchAll(/(\d)\s+(langer|lange|kurzer|kurze)\s+(ton|töne)\b/g)) {
    const count = zahl[teil[1]!] ?? 1
    pattern.push({ kind: teil[2]!.startsWith('lang') ? 'lang' : 'kurz', count })
  }
  if (pattern.length === 0) throw new Error(`Kein Muster in "${beschreibung}"`)
  return { pattern, repeatS }
}

/** Die Zeitleiste eines Binnen-Schallzeichens, ohne die Wiederholung. */
export function binnenTimeline(signal: Pick<BinnenSignalData, 'pattern'>): SignalStep[] {
  const steps: SignalStep[] = []
  for (const [index, element] of signal.pattern.entries()) {
    if (index > 0) steps.push({ kind: 'silence', duration: PAUSE_S })
    for (let i = 0; i < element.count; i += 1) {
      switch (element.kind) {
        case 'kurz':
          if (i > 0) steps.push({ kind: 'silence', duration: PAUSE_S })
          steps.push({ kind: 'short', duration: KURZ_S })
          break
        case 'lang':
          if (i > 0) steps.push({ kind: 'silence', duration: PAUSE_S })
          steps.push({ kind: 'prolonged', duration: LANG_S })
          break
        case 'sehr-kurz':
          if (i > 0) steps.push({ kind: 'silence', duration: SEHR_KURZ_PAUSE_S })
          steps.push({ kind: 'short', duration: SEHR_KURZ_S })
          break
        case 'glocke':
          if (i > 0) steps.push({ kind: 'silence', duration: PAUSE_S })
          steps.push({ kind: 'bell', duration: GLOCKE_S })
          break
      }
    }
  }
  return steps
}

/** Wie lange ein Zeichen einmal klingt, in Sekunden. */
export function binnenDauerS(signal: Pick<BinnenSignalData, 'pattern'>): number {
  return binnenTimeline(signal).reduce((summe, schritt) => summe + schritt.duration, 0)
}

/** Die Fundstelle, wenn die Anlage selbst keine nennt: die Allgemeinen Zeichen in A. */
export const FUNDSTELLE_A = '§ 4.02 Nummer 1, Anlage 6 Abschnitt A'

/**
 * Prüft ein Schallzeichen gegen die Einträge der Anlage 6 und liefert, was nicht stimmt.
 *
 * Gefragt ist, ob das Zeichen seinen Wortlaut, seine Fundstelle und sein Muster in der
 * Anlage wiederfindet, und ob Kleinfahrzeug und Lichtzeichen den zwei Sätzen aus Kapitel
 * 4 folgen. Die Prüfung stand zuerst nur im Skript der Datenprüfung und war damit selbst
 * ungeprüft: ein Verifier hat sie abgeschwächt, und eine falsche Bedeutung beim
 * Allgemeinen Zeichen "Achtung" ging durch, weil dessen Muster in Abschnitt A eindeutig
 * ist. Als reine Funktion lässt sie sich mit einem falschen Zeichen füttern.
 */
export function pruefeBinnenZeichen(zeichen: BinnenSignalData, eintraege: Anlage6Eintrag[]): string[] {
  const fehler: string[] = []
  const treffer = eintraege.filter(
    (e) => e.abschnitt === zeichen.abschnitt && (e.bedeutung ?? e.lage) === zeichen.bedeutung,
  )
  if (treffer.length === 0) {
    return [`der Wortlaut "${zeichen.bedeutung}" steht nicht in Anlage 6 Abschnitt ${zeichen.abschnitt}`]
  }
  // Derselbe Wortlaut kann zweimal vorkommen (Notsignal mit Horn und mit Glocke): dann
  // entscheidet das Muster, welcher Eintrag gemeint ist.
  const passend = treffer.find(
    (e) => JSON.stringify(musterAus(e.beschreibung).pattern) === JSON.stringify(zeichen.pattern),
  )
  if (!passend) {
    return [`das Muster folgt aus keiner Beschreibung der Anlage (${treffer.map((e) => e.beschreibung).join(' / ')})`]
  }
  const fundstelle = passend.fundstelle ?? FUNDSTELLE_A
  if (zeichen.fundstelle !== fundstelle) {
    fehler.push(`Fundstelle "${zeichen.fundstelle}", die Anlage sagt "${fundstelle}"`)
  }
  if (zeichen.kleinfahrzeug !== (zeichen.abschnitt === 'A')) {
    fehler.push('§ 4.02 Nummer 2 erlaubt Kleinfahrzeugen nur die Allgemeinen Zeichen')
  }
  const glocke = zeichen.pattern.some((x) => x.kind === 'glocke')
  if (zeichen.lichtzeichen !== (zeichen.pattern.length > 0 && !glocke)) {
    fehler.push('das Lichtzeichen nach § 4.01 Nummer 2 steht falsch')
  }
  return fehler
}
